import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { finishAuthAndRedirect } from '../utils/postAuthRedirect';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
      return null;
    } finally {
      setLoading(false);
    }
  };

  const navigateAfterGoogleLogin = async (userData, navigate) => {
    await finishAuthAndRedirect(navigate, userData);
  };

  const loginWithGoogle = async (credential, flow, inviteToken, accountId) => {
    try {
      const body = { credential, flow };
      if (inviteToken) body.invite_token = inviteToken;
      if (accountId) body.account_id = accountId;
      const response = await axios.post('/api/auth/google', body);

      // One Google identity, more than one account: a parent who also works for the district.
      // No session is created yet - the caller shows the chooser and signs in again naming the
      // account, so we never land someone in a capacity they did not pick.
      if (response.data?.choose_account) {
        return { success: false, chooseAccount: true, accounts: response.data.accounts || [] };
      }

      const { token: newToken, user: userData } = response.data;
      if (!newToken || !userData?.role) {
        return { success: false, error: 'Invalid server response' };
      }
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true, user: userData };
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Google sign-in failed';
      return { success: false, error: msg };
    }
  };

  const [switching, setSwitching] = useState(false);

  /**
   * Swap to another account belonging to the same person, in place.
   *
   * Single-page by decision - no second tab. The old token is replaced rather than kept, so
   * there is never more than one live capacity, and district-scoped data cached under the
   * previous session must not survive the swap.
   */
  const switchAccount = async (accountId) => {
    // The switcher renders INSIDE the dashboard it is leaving, and that dashboard is wrapped in
    // a role guard. The instant the new role lands in state, the guard for the route we are
    // still on fails and redirects to /login - before any navigate() can run. Reordering does
    // not help: navigating first fails the destination's guard instead. So the guards are held
    // off for the duration of the swap, exactly as they already are during initial load.
    setSwitching(true);
    // If no route ever accepts the new role, the flag would pin the app on "Loading" for good.
    // This is a backstop, not the normal path: PrivateRoute clears it as soon as it renders.
    const stuckGuard = setTimeout(() => setSwitching(false), 8000);
    try {
      const response = await axios.post('/api/auth/switch-account', { account_id: accountId });
      const { token: newToken, user: userData } = response.data;
      if (!newToken || !userData?.role) return { success: false, error: 'Invalid server response' };

      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      clearTimeout(stuckGuard);
      return { success: true, user: userData };
    } catch (error) {
      clearTimeout(stuckGuard);
      setSwitching(false);
      return {
        success: false,
        error: error.response?.data?.error || 'Could not switch account'
      };
    }
  };

  /** Called by the switcher once it has navigated, to let the route guards run again. */
  const finishSwitch = () => setSwitching(false);

  const linkGoogle = async (credential) => {
    try {
      await axios.post('/api/auth/google/link', { credential });
      await fetchUser();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to link Google'
      };
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      const data = response.data;
      // Handle both { token, user } and { data: { token, user } } response shapes
      const payload = data && typeof data === 'object' ? (data.data || data) : null;
      const newToken = payload?.token;
      const userData = payload?.user;
      if (!newToken || !userData || typeof userData !== 'object' || !userData.role) {
        console.error('Login: invalid response shape', {
          dataType: typeof data,
          hasUser: !!userData,
          userDataType: typeof userData,
          dataSample: typeof data === 'string' ? data.substring(0, 100) : JSON.stringify(data)?.substring(0, 200),
        });
        return { success: false, error: 'Invalid server response. Check console for details.' };
      }
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true, user: userData };
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.error;
      const networkErr = error.code === 'ERR_NETWORK' || error.message?.includes('Network Error');
      let userMsg = msg || 'Login failed';
      if (networkErr) userMsg = 'Cannot reach server. Is the backend running on port 5001?';
      else if (status === 401) userMsg = msg || 'Invalid credentials';
      else if (status >= 500) userMsg = 'Server error. Check backend logs.';
      console.error('Login failed:', { status, msg, networkErr, error: error.message });
      return { success: false, error: userMsg };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Set auth from registration or other external source (e.g. token + user from register-parent)
  const setAuth = (newToken, userData) => {
    if (!newToken || !userData?.role) return;
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  // Expose fetchUser so components can refresh user data (e.g. after logo upload)
  const refreshUser = async () => {
    return await fetchUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithGoogle,
    switchAccount,
    switching,
    finishSwitch,
        linkGoogle,
        navigateAfterGoogleLogin,
        logout,
        setAuth,
        token,
        refreshUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

